import React, { useState, useEffect, useMemo } from 'react';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CircularProgress from '@mui/material/CircularProgress';
import { apiClient } from '../services/apiClient';
import {
  Alert,
  Snackbar,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';

const DEFAULT_LIST_ID = '__default__';
const MAX_KEYTERMS_PER_LIST = 100;

const DEFAULT_STT_KEYTERMS = {
  default: ['Sensodyne'],
  byRole: {}
};

const parseKeytermsValue = (raw) => {
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!parsed || typeof parsed !== 'object') {
      return { default: [...DEFAULT_STT_KEYTERMS.default], byRole: {} };
    }
    const byRole = {};
    if (parsed.byRole && typeof parsed.byRole === 'object') {
      Object.entries(parsed.byRole).forEach(([role, list]) => {
        if (Array.isArray(list)) {
          byRole[role] = list.map(String).map((t) => t.trim()).filter(Boolean).slice(0, MAX_KEYTERMS_PER_LIST);
        }
      });
    }
    return {
      default: Array.isArray(parsed.default)
        ? parsed.default.map(String).map((t) => t.trim()).filter(Boolean).slice(0, MAX_KEYTERMS_PER_LIST)
        : [...DEFAULT_STT_KEYTERMS.default],
      byRole
    };
  } catch {
    return { default: [...DEFAULT_STT_KEYTERMS.default], byRole: {} };
  }
};

const KeytermPillsEditor = ({ terms, draft, onDraftChange, onAdd, onRemove, placeholder, maxTerms = MAX_KEYTERMS_PER_LIST }) => {
  const atLimit = terms.length >= maxTerms;
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2 min-h-[36px] p-2 rounded-md border border-gray-200 bg-white">
        {terms.length === 0 ? (
          <span className="text-xs text-gray-400 italic self-center">Aucun terme</span>
        ) : (
          terms.map((term) => (
            <span
              key={term}
              className="inline-flex items-center gap-1 rounded-full bg-[rgb(141,212,209)]/40 px-2.5 py-1 text-sm text-gray-800"
            >
              {term}
              <button
                type="button"
                onClick={() => onRemove(term)}
                className="ml-0.5 rounded-full p-0.5 hover:bg-gray-300/60 focus:outline-none"
                aria-label={`Supprimer ${term}`}
              >
                <CloseIcon style={{ fontSize: 14 }} />
              </button>
            </span>
          ))
        )}
      </div>
      <div className="flex items-center justify-between text-xs text-gray-500 px-0.5">
        <span>{terms.length} / {maxTerms} termes</span>
        {atLimit && <span className="text-amber-600">Limite atteinte</span>}
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          value={draft}
          onChange={(e) => onDraftChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (!atLimit) onAdd();
            }
          }}
          disabled={atLimit}
          className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
          placeholder={atLimit ? `Maximum ${maxTerms} termes` : (placeholder || 'Nouveau terme')}
        />
        <button
          type="button"
          onClick={onAdd}
          disabled={atLimit}
          className="inline-flex items-center rounded-md bg-[rgb(141,212,209)] px-3 py-2 text-sm font-medium text-gray-700 hover:bg-[rgb(121,192,189)] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <AddIcon className="h-4 w-4 mr-1" />
          Ajouter
        </button>
      </div>
    </div>
  );
};

export default function Settings() {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [settingForm, setSettingForm] = useState({
    key: '',
    value: '',
    description: ''
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [subRoles, setSubRoles] = useState([]);

  // Keyterms editor state
  const [keytermsConfig, setKeytermsConfig] = useState({
    default: [...DEFAULT_STT_KEYTERMS.default],
    byRole: {}
  });
  const [selectedRole, setSelectedRole] = useState('');
  const [defaultDraft, setDefaultDraft] = useState('');
  const [roleDraft, setRoleDraft] = useState('');
  const [copyFrom, setCopyFrom] = useState(DEFAULT_LIST_ID);
  const [copyTo, setCopyTo] = useState('');
  const [openCopyConfirmDialog, setOpenCopyConfirmDialog] = useState(false);
  const [copyOverwriteTerms, setCopyOverwriteTerms] = useState([]);

  const listOptions = useMemo(() => {
    const options = [{ id: DEFAULT_LIST_ID, label: 'Liste par défaut' }];
    subRoles.forEach((role) => {
      options.push({ id: role, label: `Sous-rôle : ${role}` });
    });
    return options;
  }, [subRoles]);

  useEffect(() => {
    fetchData();
    fetchSubRoles();
  }, []);

  useEffect(() => {
    if (subRoles.length > 0 && !selectedRole) {
      setSelectedRole(subRoles[0]);
      if (!copyTo) {
        setCopyTo(subRoles[0]);
      }
    }
  }, [subRoles, selectedRole, copyTo]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const data = await apiClient.get('/api/admin/settings');
      setSettings(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching settings:', err);
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubRoles = async () => {
    try {
      const data = await apiClient.get('/api/admin/roles/sub-roles');
      setSubRoles(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching sub-roles:', err);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const initKeytermsEditor = (rawValue) => {
    const parsed = parseKeytermsValue(rawValue || JSON.stringify(DEFAULT_STT_KEYTERMS));
    setKeytermsConfig(parsed);
    setDefaultDraft('');
    setRoleDraft('');
    const role = subRoles[0] || '';
    setSelectedRole(role);
    setCopyFrom(DEFAULT_LIST_ID);
    setCopyTo(role || DEFAULT_LIST_ID);
    return parsed;
  };

  const getListById = (config, listId) => {
    if (listId === DEFAULT_LIST_ID) {
      return [...(config.default || [])];
    }
    return [...(config.byRole?.[listId] || [])];
  };

  const setListById = (config, listId, terms) => {
    if (listId === DEFAULT_LIST_ID) {
      return { ...config, default: terms };
    }
    const byRole = { ...config.byRole };
    if (terms.length === 0) {
      delete byRole[listId];
    } else {
      byRole[listId] = terms;
    }
    return { ...config, byRole };
  };

  const addTerm = (listId, draft, clearDraft) => {
    const term = String(draft || '').trim();
    if (!term) return;
    setKeytermsConfig((prev) => {
      const current = getListById(prev, listId);
      if (current.length >= MAX_KEYTERMS_PER_LIST) {
        return prev;
      }
      if (current.some((t) => t.toLowerCase() === term.toLowerCase())) {
        return prev;
      }
      return setListById(prev, listId, [...current, term]);
    });
    clearDraft('');
  };

  const removeTerm = (listId, term) => {
    setKeytermsConfig((prev) => {
      const current = getListById(prev, listId).filter((t) => t !== term);
      return setListById(prev, listId, current);
    });
  };

  const persistKeytermsConfig = async (config, { closeDialog = false } = {}) => {
    if (!config.default.length) {
      showSnackbar('La liste par défaut des keyterms est obligatoire', 'error');
      return false;
    }
    if (!settingForm.key && !selectedItem) {
      showSnackbar('Définissez d\'abord la clé du paramètre', 'error');
      return false;
    }

    const valueToSend = JSON.stringify({
      default: config.default,
      byRole: config.byRole
    });
    const descriptionToSend = selectedItem?.description
      || settingForm.description
      || 'Deepgram Nova-3 keyterms: mandatory default list, optional byRole maps (consultation sub-role).';
    const key = selectedItem?.key || settingForm.key;

    try {
      setSaving(true);
      if (selectedItem) {
        await apiClient.put(`/api/admin/settings/${selectedItem.key}`, {
          value: valueToSend,
          description: descriptionToSend
        });
        setSelectedItem((prev) => (prev ? { ...prev, value: valueToSend, description: descriptionToSend } : prev));
      } else {
        await apiClient.post('/api/admin/settings', {
          key,
          value: valueToSend,
          description: descriptionToSend
        });
      }
      setSettingForm((prev) => ({ ...prev, value: valueToSend, description: descriptionToSend }));
      await fetchData();
      if (closeDialog) {
        handleCloseDialog();
      }
      return true;
    } catch (err) {
      console.error('Error saving keyterms setting:', err);
      if (err.response?.status === 409) {
        showSnackbar('Cette clé existe déjà', 'error');
      } else {
        showSnackbar('Erreur lors de l\'enregistrement', 'error');
      }
      return false;
    } finally {
      setSaving(false);
    }
  };

  const applyCopyAndSave = async () => {
    if (!copyFrom || !copyTo) {
      showSnackbar('Sélectionnez une source et une destination', 'error');
      return;
    }
    if (copyFrom === copyTo) {
      showSnackbar('La source et la destination doivent être différentes', 'error');
      return;
    }

    const source = getListById(keytermsConfig, copyFrom).slice(0, MAX_KEYTERMS_PER_LIST);
    const nextConfig = setListById(keytermsConfig, copyTo, [...source]);
    setKeytermsConfig(nextConfig);

    const fromLabel = listOptions.find((o) => o.id === copyFrom)?.label || copyFrom;
    const toLabel = listOptions.find((o) => o.id === copyTo)?.label || copyTo;

    const saved = await persistKeytermsConfig(nextConfig, { closeDialog: false });
    if (saved) {
      showSnackbar(`Liste copiée et enregistrée : ${fromLabel} → ${toLabel}`);
    }
  };

  const handleCopyList = () => {
    if (!copyFrom || !copyTo) {
      showSnackbar('Sélectionnez une source et une destination', 'error');
      return;
    }
    if (copyFrom === copyTo) {
      showSnackbar('La source et la destination doivent être différentes', 'error');
      return;
    }

    const destTerms = getListById(keytermsConfig, copyTo);
    if (destTerms.length > 0) {
      setCopyOverwriteTerms(destTerms);
      setOpenCopyConfirmDialog(true);
      return;
    }

    applyCopyAndSave();
  };

  const handleConfirmCopyOverwrite = async () => {
    setOpenCopyConfirmDialog(false);
    await applyCopyAndSave();
    setCopyOverwriteTerms([]);
  };

  const handleCancelCopyOverwrite = () => {
    setOpenCopyConfirmDialog(false);
    setCopyOverwriteTerms([]);
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setSelectedItem(item);
      setSettingForm({
        key: item.key,
        value: item.value,
        description: item.description || ''
      });
      if (item.key === 'stt-keyterms') {
        initKeytermsEditor(item.value);
      }
    } else {
      setSelectedItem(null);
      setSettingForm({
        key: '',
        value: '',
        description: ''
      });
      initKeytermsEditor(JSON.stringify(DEFAULT_STT_KEYTERMS));
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedItem(null);
    setSettingForm({
      key: '',
      value: '',
      description: ''
    });
  };

  const handleOpenDeleteDialog = (item) => {
    setSelectedItem(item);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setSelectedItem(null);
  };

  const handleKeyChange = (newKey) => {
    if (newKey === 'stt-keyterms') {
      const parsed = initKeytermsEditor(JSON.stringify(DEFAULT_STT_KEYTERMS));
      setSettingForm({
        key: newKey,
        value: JSON.stringify(parsed),
        description: ''
      });
      return;
    }
    setSettingForm({ ...settingForm, key: newKey });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);

      let valueToSend = settingForm.value;
      let descriptionToSend = settingForm.description;

      if (settingForm.key === 'stt-engine') {
        valueToSend = `"${settingForm.value.replace(/^"|"$/g, '')}"`;
      } else if (settingForm.key === 'stt-keyterms') {
        const saved = await persistKeytermsConfig(keytermsConfig, { closeDialog: true });
        if (saved) {
          showSnackbar(selectedItem ? 'Paramètre mis à jour avec succès' : 'Paramètre créé avec succès');
        }
        return;
      }

      if (selectedItem) {
        await apiClient.put(`/api/admin/settings/${selectedItem.key}`, {
          value: valueToSend,
          description: descriptionToSend
        });
      } else {
        await apiClient.post('/api/admin/settings', {
          key: settingForm.key,
          value: valueToSend,
          description: descriptionToSend
        });
      }
      await fetchData();
      handleCloseDialog();
      showSnackbar(selectedItem ? 'Paramètre mis à jour avec succès' : 'Paramètre créé avec succès');
    } catch (err) {
      console.error('Error saving setting:', err);
      if (err.response?.status === 409) {
        showSnackbar('Cette clé existe déjà', 'error');
      } else {
        showSnackbar('Erreur lors de l\'enregistrement', 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      setSaving(true);
      await apiClient.delete(`/api/admin/settings/${selectedItem.key}`);
      await fetchData();
      handleCloseDeleteDialog();
      showSnackbar('Paramètre supprimé avec succès');
    } catch (err) {
      console.error('Error deleting setting:', err);
      showSnackbar('Erreur lors de la suppression', 'error');
    } finally {
      setSaving(false);
    }
  };

  const formatValue = (value) => {
    try {
      const parsed = JSON.parse(value);
      if (typeof parsed === 'string') {
        return parsed;
      }
      return JSON.stringify(parsed, null, 2);
    } catch {
      return value;
    }
  };

  const roleTerms = selectedRole ? (keytermsConfig.byRole[selectedRole] || []) : [];

  const renderValueInput = () => {
    if (settingForm.key === 'stt-engine') {
      return (
        <FormControl fullWidth size="small">
          <InputLabel id="stt-engine-label">Moteur STT</InputLabel>
          <Select
            labelId="stt-engine-label"
            id="stt-engine"
            value={settingForm.value.replace(/^"|"$/g, '')}
            onChange={(e) => setSettingForm({ ...settingForm, value: e.target.value })}
            required
            label="Moteur STT"
          >
            <MenuItem value="azure">Azure</MenuItem>
            <MenuItem value="deepgram">Deepgram</MenuItem>
            <MenuItem value="webspeech" disabled>
              Web Speech
            </MenuItem>
          </Select>
        </FormControl>
      );
    }

    if (settingForm.key === 'stt-keyterms') {
      return (
        <div className="space-y-5">
          {/* Default list section */}
          <section className="rounded-lg border-2 border-[rgb(141,212,209)] bg-[rgb(141,212,209)]/10 p-4">
            <h4 className="text-sm font-semibold text-gray-900 mb-1">
              Liste par défaut
            </h4>
            <p className="text-xs text-gray-600 mb-3">
              Obligatoire. Utilisée quand un sous-rôle n&apos;a pas de liste dédiée.
            </p>
            <KeytermPillsEditor
              terms={keytermsConfig.default}
              draft={defaultDraft}
              onDraftChange={setDefaultDraft}
              onAdd={() => addTerm(DEFAULT_LIST_ID, defaultDraft, setDefaultDraft)}
              onRemove={(term) => removeTerm(DEFAULT_LIST_ID, term)}
              placeholder="Ex: Sensodyne"
            />
          </section>

          {/* Role list section */}
          <section className="rounded-lg border border-gray-200 bg-gray-50 p-4">
            <h4 className="text-sm font-semibold text-gray-900 mb-1">
              Listes par sous-rôle
            </h4>
            <p className="text-xs text-gray-600 mb-3">
              Optionnel. Liste vide = fallback sur la liste par défaut.
            </p>

            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel id="keyterms-role-label">Sous-rôle</InputLabel>
              <Select
                labelId="keyterms-role-label"
                id="keyterms-role"
                value={selectedRole || ''}
                onChange={(e) => setSelectedRole(e.target.value)}
                label="Sous-rôle"
              >
                {subRoles.map((role) => (
                  <MenuItem key={role} value={role}>{role}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedRole ? (
              <div className="mt-1">
                <KeytermPillsEditor
                  terms={roleTerms}
                  draft={roleDraft}
                  onDraftChange={setRoleDraft}
                  onAdd={() => addTerm(selectedRole, roleDraft, setRoleDraft)}
                  onRemove={(term) => removeTerm(selectedRole, term)}
                  placeholder={`Terme pour ${selectedRole}`}
                />
              </div>
            ) : (
              <p className="text-xs text-gray-500 italic mt-2">Aucun sous-rôle disponible.</p>
            )}
          </section>

          {/* Copy section */}
          <section className="rounded-lg border border-dashed border-gray-300 bg-white p-4">
            <h4 className="text-sm font-semibold text-gray-900 mb-3">
              Copier une liste
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              <FormControl fullWidth size="small">
                <InputLabel id="copy-from-label">Depuis</InputLabel>
                <Select
                  labelId="copy-from-label"
                  value={copyFrom}
                  onChange={(e) => setCopyFrom(e.target.value)}
                  label="Depuis"
                >
                  {listOptions.map((opt) => (
                    <MenuItem key={opt.id} value={opt.id}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel id="copy-to-label">Vers</InputLabel>
                <Select
                  labelId="copy-to-label"
                  value={copyTo}
                  onChange={(e) => setCopyTo(e.target.value)}
                  label="Vers"
                >
                  {listOptions.map((opt) => (
                    <MenuItem key={opt.id} value={opt.id}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <button
              type="button"
              onClick={handleCopyList}
              disabled={saving}
              className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ContentCopyIcon className="h-4 w-4 mr-2" />
              {saving ? 'Enregistrement...' : 'Copier la liste'}
            </button>
          </section>
        </div>
      );
    }

    return (
      <textarea
        id="value"
        name="value"
        rows={3}
        value={settingForm.value}
        onChange={(e) => setSettingForm({ ...settingForm, value: e.target.value })}
        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
        required
        placeholder='Ex: "azure" ou {"engine": "azure", "config": {...}}'
      />
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  const isKeytermsDialog = settingForm.key === 'stt-keyterms';
  const hideDescription = isKeytermsDialog || settingForm.key === 'stt-engine';

  return (
    <div className="bg-gray-100 rounded-lg">
      <div className="px-6 py-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-medium text-gray-900">
            Gestion des paramètres
          </h2>
          <button
            onClick={() => handleOpenDialog()}
            className="inline-flex items-center justify-center rounded-md bg-[rgb(141,212,209)] px-4 py-2 text-sm font-medium text-gray-700 hover:bg-[rgb(121,192,189)] focus:outline-none focus:ring-2 focus:ring-[rgb(141,212,209)] focus:ring-offset-2"
          >
            <AddIcon className="-ml-1 mr-2 h-5 w-5" />
            Nouveau paramètre
          </button>
        </div>

        {error && (
          <Alert severity="error" className="mb-4">{error}</Alert>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Clé
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Valeur
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {settings.map((setting) => (
                <tr key={setting.key} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {setting.key}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    <div className="max-w-xs truncate" title={formatValue(setting.value)}>
                      {formatValue(setting.value)}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    <div className="max-w-xs truncate" title={setting.description}>
                      {setting.description || '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleOpenDialog(setting)}
                      className="text-blue-600 hover:text-blue-900 mr-2"
                    >
                      <EditIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleOpenDeleteDialog(setting)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <DeleteIcon className="h-5 w-5" />
                    </button>
                  </td>
                </tr>
              ))}
              {settings.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-6 py-4 text-center text-sm text-gray-500">
                    Aucun paramètre trouvé
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {openDialog && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50 ml-[240px]">
          <div className={`bg-white rounded-lg w-full max-h-[90vh] overflow-y-auto ${isKeytermsDialog ? 'max-w-xl' : 'max-w-md'}`}>
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                {isKeytermsDialog
                  ? (selectedItem ? 'Keyterms pour la reconnaissance vocale' : 'Créer les keyterms pour la reconnaissance vocale')
                  : (selectedItem ? 'Modifier le paramètre' : 'Créer un paramètre')}
              </h3>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="px-6 py-4">
                <div className="space-y-4">
                  {(!selectedItem || !isKeytermsDialog) && (
                    <div>
                      <label htmlFor="key" className="block text-sm font-medium text-gray-700 mb-1">
                        Clé
                      </label>
                      <input
                        type="text"
                        id="key"
                        name="key"
                        value={settingForm.key}
                        onChange={(e) => handleKeyChange(e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        required
                        disabled={!!selectedItem}
                        list="known-setting-keys"
                      />
                      <datalist id="known-setting-keys">
                        <option value="stt-engine" />
                        <option value="stt-keyterms" />
                      </datalist>
                    </div>
                  )}

                  {isKeytermsDialog && selectedItem && (
                    <p className="text-xs text-gray-500">
                      Clé : <span className="font-mono font-medium text-gray-700">stt-keyterms</span>
                    </p>
                  )}

                  <div>
                    {!isKeytermsDialog && (
                      <label htmlFor="value" className="block text-sm font-medium text-gray-700 mb-1">
                        Valeur
                      </label>
                    )}
                    {renderValueInput()}
                  </div>

                  {!hideDescription && (
                    <div>
                      <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <textarea
                        id="description"
                        name="description"
                        rows={2}
                        value={settingForm.description}
                        onChange={(e) => setSettingForm({ ...settingForm, description: e.target.value })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        placeholder="Description optionnelle du paramètre"
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-3 rounded-b-lg">
                <button
                  type="button"
                  onClick={handleCloseDialog}
                  className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className={`inline-flex justify-center rounded-md bg-[rgb(141,212,209)] px-4 py-2 text-sm font-medium text-gray-700 
                    ${!saving ? 'hover:bg-[rgb(121,192,189)]' : 'opacity-75 cursor-not-allowed'}
                    focus:outline-none focus:ring-2 focus:ring-[rgb(141,212,209)] focus:ring-offset-2`}
                >
                  {saving ? (
                    <>
                      <CircularProgress size={20} className="mr-2" />
                      Enregistrement...
                    </>
                  ) : (
                    selectedItem ? 'Enregistrer' : 'Créer'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {openCopyConfirmDialog && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-[60] ml-[240px]">
          <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                Remplacer les keyterms existants ?
              </h3>
            </div>

            <div className="px-6 py-4 space-y-3">
              <p className="text-sm text-gray-700">
                La destination{' '}
                <span className="font-medium">
                  {listOptions.find((o) => o.id === copyTo)?.label || copyTo}
                </span>{' '}
                contient déjà {copyOverwriteTerms.length} terme
                {copyOverwriteTerms.length > 1 ? 's' : ''}.
                La copie depuis{' '}
                <span className="font-medium">
                  {listOptions.find((o) => o.id === copyFrom)?.label || copyFrom}
                </span>{' '}
                effacera ces termes et enregistrera automatiquement la nouvelle liste.
              </p>
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
                <p className="text-xs font-semibold text-amber-800 mb-2 uppercase tracking-wide">
                  Termes qui seront effacés
                </p>
                <div className="flex flex-wrap gap-1.5 max-h-48 overflow-y-auto">
                  {copyOverwriteTerms.map((term) => (
                    <span
                      key={term}
                      className="inline-flex rounded-full bg-white border border-amber-200 px-2.5 py-0.5 text-sm text-gray-800"
                    >
                      {term}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-3 rounded-b-lg">
              <button
                type="button"
                onClick={handleCancelCopyOverwrite}
                disabled={saving}
                className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleConfirmCopyOverwrite}
                disabled={saving}
                className="inline-flex justify-center rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <CircularProgress size={20} className="mr-2" />
                    Copie...
                  </>
                ) : (
                  'Confirmer la copie'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {openDeleteDialog && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50 ml-[240px]">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                Confirmer la suppression
              </h3>
            </div>

            <div className="px-6 py-4">
              <p className="text-gray-700">
                Êtes-vous sûr de vouloir supprimer le paramètre &quot;{selectedItem?.key}&quot; ?
              </p>
            </div>

            <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-3 rounded-b-lg">
              <button
                type="button"
                onClick={handleCloseDeleteDialog}
                className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Annuler
              </button>
              <button
                onClick={handleDelete}
                disabled={saving}
                className="inline-flex justify-center rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              >
                {saving ? (
                  <>
                    <CircularProgress size={20} className="mr-2" />
                    Suppression...
                  </>
                ) : (
                  'Supprimer'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
